/**
 * Organization Service
 * Handles all organization-related API calls
 * @module services/organizations/organizationService
 */

import httpClient from '@/utils/httpClient';
import { isValidId, createInvalidIdResponse } from '@/services/shared/validationHelpers';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';

const organizationService = {
  /**
   * Get organization settings
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} - Response data containing organization settings
   * @example
   * {
   *   "success": true,
   *   "message": "تم جلب إعدادات المنظمة بنجاح",
   *   "data": {
   *     "organizationId": "...",
   *     "organizationName": "...",
   *     "timezone": "...",
   *     "workStart": "...",
   *     "workEnd": "...",
   *     "workDays": [...],
   *     "gracePeriod": 30,
   *     "orgSettings": {...}
   *   }
   * }
   */
  getSettings: async (orgId) => {
    if (!isValidId(orgId, 'Organization')) {
      return createInvalidIdResponse('Organization');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.ORGANIZATIONS.GET_SETTINGS, orgId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update organization settings
   * @param {string} orgId - Organization ID
   * @param {Object} settingsData - Settings data to update
   * @returns {Promise<Object>} - Response data
   */
  updateSettings: async (orgId, settingsData) => {
    if (!isValidId(orgId, 'Organization')) {
      return createInvalidIdResponse('Organization');
    }
    try {
      const response = await httpClient.patch(
        getEndpointPath(ENDPOINTS.ORGANIZATIONS.SETTINGS, orgId),
        settingsData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get current organization data
   * GET /api/organizations/me
   * @returns {Promise<Object>} - Response data containing organization information
   */
  getMe: async () => {
    try {
      const response = await httpClient.get('/organizations/me');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update current organization data
   * PATCH /api/organizations/me
   * @param {Object} organizationData - Organization data to update
   * @param {string} [organizationData.workStart] - Work start time (e.g., "09:00")
   * @param {string} [organizationData.workEnd] - Work end time (e.g., "17:00")
   * @param {string[]} [organizationData.workDays] - Array of work days (e.g., ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"])
   * @param {number} [organizationData.gracePeriod] - Grace period in minutes
   * @returns {Promise<Object>} - Response data
   * @example
   * {
   *   workStart: "09:00",
   *   workEnd: "17:00",
   *   workDays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
   *   gracePeriod: 15
   * }
   */
  updateMe: async (organizationData) => {
    try {
      const response = await httpClient.patch('/organizations/me', organizationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get organization late penalty settings
   * @returns {Promise<Object>} - Response data containing late penalty settings
   * @example
   * {
   *   "success": true,
   *   "message": "تم جلب إعدادات عقوبات التأخير للمنظمة بنجاح",
   *   "data": {
   *     "organizationId": "...",
   *     "organizationName": "...",
   *     "orgId": "...",
   *     "latePenaltySettings": {
   *       "rules": [...],
   *       "enabled": false
   *     }
   *   }
   * }
   */
  getLatePenaltySettings: async () => {
    try {
      const response = await httpClient.get(ENDPOINTS.ORGANIZATIONS.LATE_PENALTY_SETTINGS);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update organization late penalty settings
   * @param {Object} latePenaltySettings - Late penalty settings object
   * @param {boolean} latePenaltySettings.enabled - Whether late penalty is enabled
   * @param {Array} latePenaltySettings.rules - Array of penalty rules
   * @param {number} rules[].delayMinutes - Delay in minutes
   * @param {string} rules[].penaltyType - Penalty type ('quarter_day', 'half_day', 'full_day', 'custom')
   * @param {number|null} rules[].penaltyAmount - Penalty amount (for custom type)
   * @param {number|null} rules[].penaltyDays - Penalty days (for custom type)
   * @returns {Promise<Object>} - Response data
   */
  updateLatePenaltySettings: async (latePenaltySettings) => {
    try {
      const response = await httpClient.patch(
        ENDPOINTS.ORGANIZATIONS.LATE_PENALTY_SETTINGS,
        latePenaltySettings
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get organization auto-checkout settings
   * @returns {Promise<Object>} - Response data
   */
  getAutoCheckoutSettings: async () => {
    try {
      const response = await httpClient.get(ENDPOINTS.ORGANIZATIONS.AUTO_CHECKOUT_SETTINGS);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update organization auto-checkout settings
   * @param {Object} autoCheckoutSettings
   * @param {boolean} [autoCheckoutSettings.enabled]
   * @param {number} [autoCheckoutSettings.graceHours] - Hours after shift end before auto-checkout (0-24)
   * @param {boolean} [autoCheckoutSettings.countOvertime]
   * @param {boolean} [autoCheckoutSettings.notifyEmployee]
   * @param {boolean} [autoCheckoutSettings.notifyAdmin]
   * @returns {Promise<Object>} - Response data
   */
  updateAutoCheckoutSettings: async (autoCheckoutSettings) => {
    try {
      const response = await httpClient.patch(
        ENDPOINTS.ORGANIZATIONS.AUTO_CHECKOUT_SETTINGS,
        autoCheckoutSettings
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get organization auto-checkout deduction policy
   * @returns {Promise<Object>}
   */
  getAutoCheckoutDeductionSettings: async () => {
    try {
      const response = await httpClient.get(ENDPOINTS.ORGANIZATIONS.AUTO_CHECKOUT_DEDUCTION_SETTINGS);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update organization auto-checkout deduction policy
   * @param {Object} autoCheckoutDeductionSettings
   * @param {'free'|'quarter_day'|'half_day'|'fixed_amount'} autoCheckoutDeductionSettings.policy
   * @param {number} [autoCheckoutDeductionSettings.fixedAmount]
   * @returns {Promise<Object>}
   */
  updateAutoCheckoutDeductionSettings: async (autoCheckoutDeductionSettings) => {
    try {
      const response = await httpClient.patch(
        ENDPOINTS.ORGANIZATIONS.AUTO_CHECKOUT_DEDUCTION_SETTINGS,
        autoCheckoutDeductionSettings
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get organization check-in selfie settings
   * @returns {Promise<Object>}
   */
  getCheckInSelfieSettings: async () => {
    try {
      const response = await httpClient.get(ENDPOINTS.ORGANIZATIONS.CHECK_IN_SELFIE_SETTINGS);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update organization check-in selfie settings
   * @param {Object} checkInSelfieSettings
   * @param {'off'|'optional'|'required'} checkInSelfieSettings.mode
   * @param {string[]} checkInSelfieSettings.applyTo - office|remote|hybrid|delivery|all
   * @returns {Promise<Object>}
   */
  updateCheckInSelfieSettings: async (checkInSelfieSettings) => {
    try {
      const response = await httpClient.patch(
        ENDPOINTS.ORGANIZATIONS.CHECK_IN_SELFIE_SETTINGS,
        checkInSelfieSettings
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default organizationService;
